import React, { useState, useMemo } from "react";
import {
  Dialog, DialogContent, DialogTitle, Table, TableBody,
  TableCell, TableHead, TableRow, IconButton, TextField,
  Typography, Box, Chip, InputAdornment, Slide, Divider,
  useMediaQuery, useTheme, Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

// ─── Transition ───────────────────────────────────────────────────────────────
const SlideUp = React.forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

// ─── Detalle de observación ───────────────────────────────────────────────────
const ObservacionView = ({ item, onBack }) => (
  <Box>
    {/* Header */}
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
      <IconButton
        size="small"
        onClick={onBack}
        sx={{ bgcolor: "#f1f5f9", borderRadius: "8px", width: 34, height: 34 }}
      >
        <ArrowBackIcon sx={{ fontSize: 17, color: "#64748b" }} />
      </IconButton>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", color: "#1e293b" }}>
          {item.cache_Art?.art_descripcion}
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
          Código: {item.prd_code}
        </Typography>
      </Box>
    </Box>

    <Divider sx={{ mb: 2, borderColor: "#f1f5f9" }} />

    {/* Observación */}
    <Box sx={{
      bgcolor: "#f8fafc", borderRadius: "12px",
      border: "1.5px solid #e2e8f0", p: 2.5,
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        <CommentOutlinedIcon sx={{ fontSize: 16, color: "#6366f1" }} />
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Observación
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "0.875rem", color: "#334155", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
        {item.ocd_observacion || "Sin observación"}
      </Typography>
    </Box>
  </Box>
);

// ─── Fila de tabla (desktop) ──────────────────────────────────────────────────
const DetalleRow = ({ item, onVerObservacion }) => (
  <TableRow sx={{ "&:hover": { bgcolor: "#f8fafc" }, transition: "background 0.12s" }}>
    <TableCell sx={{ fontSize: "0.78rem", color: "#64748b", fontWeight: 600 }}>
      {item.prd_code}
    </TableCell>
    <TableCell sx={{ fontSize: "0.82rem", color: "#1e293b", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
      {item.cache_Art?.art_descripcion}
    </TableCell>
    <TableCell align="center">
      <Chip label={item.ocd_quantity} size="small" sx={{ bgcolor: "#eef2ff", color: "#6366f1", fontWeight: 700, fontSize: "0.72rem", height: 22, border: "1px solid #c7d2fe" }} />
    </TableCell>
    <TableCell sx={{ fontSize: "0.82rem", color: "#334155" }}>
      ${Number(item.ocd_price || 0).toLocaleString("es-AR")}
    </TableCell>
    <TableCell sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1e293b" }}>
      ${Number(item.ocd_total_amount || 0).toLocaleString("es-AR")}
    </TableCell>
    <TableCell align="center">
      {item.ocd_observacion ? (
        <IconButton
          size="small"
          onClick={() => onVerObservacion(item)}
          sx={{ bgcolor: "#eef2ff", borderRadius: "8px", width: 30, height: 30, "&:hover": { bgcolor: "#e0e7ff" } }}
        >
          <ChevronRightIcon sx={{ fontSize: 16, color: "#6366f1" }} />
        </IconButton>
      ) : (
        <Typography sx={{ fontSize: "0.75rem", color: "#cbd5e1" }}>—</Typography>
      )}
    </TableCell>
  </TableRow>
);

// ─── Card mobile ──────────────────────────────────────────────────────────────
const DetalleCard = ({ item, onVerObservacion }) => (
  <Paper elevation={0} sx={{
    border: "1.5px solid #e2e8f0", borderRadius: "12px",
    p: 2, mb: 1.5,
    transition: "box-shadow 0.15s",
    "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.07)" },
  }}>
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0, pr: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: "#1e293b", mb: 0.25 }}>
          {item.cache_Art?.art_descripcion}
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
          Cód: {item.prd_code}
        </Typography>
      </Box>
      <Chip label={`x${item.ocd_quantity}`} size="small" sx={{ bgcolor: "#eef2ff", color: "#6366f1", fontWeight: 700, fontSize: "0.72rem", height: 22, flexShrink: 0 }} />
    </Box>

    <Divider sx={{ my: 1, borderColor: "#f1f5f9" }} />

    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <Box sx={{ display: "flex", gap: 2.5 }}>
        <Box>
          <Typography sx={{ fontSize: "0.67rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Precio</Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "#334155" }}>
            ${Number(item.ocd_price || 0).toLocaleString("es-AR")}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.67rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>Total</Typography>
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#1e293b" }}>
            ${Number(item.ocd_total_amount || 0).toLocaleString("es-AR")}
          </Typography>
        </Box>
      </Box>
      {item.ocd_observacion && (
        <IconButton
          size="small"
          onClick={() => onVerObservacion(item)}
          sx={{ bgcolor: "#eef2ff", borderRadius: "8px", width: 34, height: 34 }}
        >
          <CommentOutlinedIcon sx={{ fontSize: 16, color: "#6366f1" }} />
        </IconButton>
      )}
    </Box>
  </Paper>
);

// ─── Modal principal ──────────────────────────────────────────────────────────
const OrderDetailModal = ({ open, onClose, pedidoDetalles = [] }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [detalleSeleccionado, setDetalleSeleccionado] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const filtrados = useMemo(() => {
    if (!searchTerm.trim()) return pedidoDetalles;
    const palabras = searchTerm.toLowerCase().split(/\s+/);
    return pedidoDetalles.filter(item => {
      const texto = `${item.prd_code} ${item.cache_Art?.art_descripcion || ""}`.toLowerCase();
      return palabras.every(p => texto.includes(p));
    });
  }, [searchTerm, pedidoDetalles]);

  const handleClose = () => {
    setSearchTerm("");
    setDetalleSeleccionado(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      TransitionComponent={isMobile ? SlideUp : undefined}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "20px",
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.2)",
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        px: 3, pt: 2.5, pb: 1.75,
        borderBottom: "1px solid #f1f5f9",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ReceiptLongOutlinedIcon sx={{ fontSize: 19, color: "#6366f1" }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1e293b", lineHeight: 1.2 }}>
              Detalle del Pedido
            </Typography>
            {!detalleSeleccionado && (
              <Typography sx={{ fontSize: "0.72rem", color: "#94a3b8" }}>
                {pedidoDetalles.length} artículo{pedidoDetalles.length !== 1 ? "s" : ""}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{ bgcolor: "#f1f5f9", borderRadius: "8px", width: 34, height: 34 }}
        >
          <CloseIcon sx={{ fontSize: 17, color: "#64748b" }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#fff" }}>

        {/* ── Vista observación ── */}
        {detalleSeleccionado ? (
          <ObservacionView item={detalleSeleccionado} onBack={() => setDetalleSeleccionado(null)} />
        ) : (
          <>
            {/* Buscador */}
            <TextField
              fullWidth size="small"
              placeholder="Buscar por código o descripción..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px", bgcolor: "#f8fafc",
                  "& fieldset": { border: "1.5px solid #e2e8f0" },
                  "&:hover fieldset": { borderColor: "#94a3b8" },
                  "&.Mui-focused fieldset": { borderColor: "#6366f1", borderWidth: "2px" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
            />

            {/* Sin resultados */}
            {filtrados.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <Typography sx={{ fontSize: "0.875rem", color: "#94a3b8" }}>
                  No se encontraron artículos
                </Typography>
              </Box>
            ) : isMobile ? (
              /* ── Mobile: cards ── */
              filtrados.map((item, i) => (
                <DetalleCard key={i} item={item} onVerObservacion={setDetalleSeleccionado} />
              ))
            ) : (
              /* ── Desktop: tabla ── */
              <Box sx={{ borderRadius: "12px", border: "1.5px solid #e2e8f0", overflow: "hidden" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#f8fafc" }}>
                      {["Código", "Artículo", "Cant.", "Precio", "Total", "Obs."].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.72rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "2px solid #e2e8f0", py: 1.25 }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtrados.map((item, i) => (
                      <DetalleRow key={i} item={item} onVerObservacion={setDetalleSeleccionado} />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default OrderDetailModal;